/**
 * Types for Experiments API (campaignmanager/v1/experiments)
 */
/** Pacing mode: whether a campaign paces to an impression goal or a spend/budget goal */
export const PacingMode = {
    IMPRESSIONS: "IMPRESSIONS",
    BUDGET: "BUDGET"
};
/** Billing type: whether a campaign is billed via rate card or cost+ (mirrors mad.Inst.ChargeType) */
export const BillingType = {
    RATE_CARD: "RATE_CARD",
    COST_PLUS: "COST_PLUS"
};
/**
 * Inventory source selectable as an eligibility filter. Deliberately narrower than the
 * generated mad.InventorySource -- only MADHIVE_PRIVATE is accepted today, enforced by the
 * CHECK constraint on experiment.eligibility_criteria.allowed_inventory_sources. Widen this
 * (and the CHECK constraint) together when another source is supported.
 */
export const InventorySource = {
    MADHIVE_PRIVATE: "MADHIVE_PRIVATE"
};
export const MediaSubType = {
    AUDIO: "AUDIO",
    VIDEO_OTT: "VIDEO_OTT",
    VIDEO_OLV: "VIDEO_OLV",
    VIDEO_OTV: "VIDEO_OTV",
    VIDEO_DOOH: "VIDEO_DOOH",
    VIDEO_NATIVE: "VIDEO_NATIVE",
    DISPLAY_DOOH: "DISPLAY_DOOH",
    DISPLAY_TRADITIONAL: "DISPLAY_TRADITIONAL",
    DISPLAY_NATIVE: "DISPLAY_NATIVE"
};
/** Bucketing strategy for experiment variant assignment */
export const BucketingStrategy = {
    UNIFORM_RANDOM: "UNIFORM_RANDOM",
    WEIGHTED_ROUND_ROBIN: "WEIGHTED_ROUND_ROBIN",
    STICKY_UNIFORM_RANDOM: "STICKY_UNIFORM_RANDOM",
    /** Every admitted entity shares one arm per time interval; see SwitchbackConfig. */
    SWITCHBACK: "SWITCHBACK"
};
/** Partition a switchback schedule runs within */
export const SwitchbackScope = {
    GLOBAL: "GLOBAL"
};
/** Shortest switchback slot beaker accepts, in milliseconds. */
export const SWITCHBACK_MIN_SLOT_MS = 60000;
/** Entity type used for bucketing decisions */
export const BucketingEntityType = {
    BUCKETING_IP_ADDRESS: "BUCKETING_IP_ADDRESS",
    BUCKETING_ORG: "BUCKETING_ORG",
    BUCKETING_CAMPAIGN: "BUCKETING_CAMPAIGN",
    BUCKETING_LINE_ITEM: "BUCKETING_LINE_ITEM",
    /** The acting user's ID from current context. */
    BUCKETING_USER: "BUCKETING_USER",
    /** The parent org (tenant) ID from current context. */
    BUCKETING_PARENT_ORG: "BUCKETING_PARENT_ORG",
    /**
     * A front-end interactive session ID. Front-end-only: back-end (bidder/roger)
     * evaluation has no session identity to bucket on and treats this as not applicable.
     */
    BUCKETING_SESSION: "BUCKETING_SESSION",
    /**
     * Bucket per individual request rather than any stable entity -- randomized on every
     * request instead of sticky to an entity ID.
     */
    BUCKETING_REQUEST: "BUCKETING_REQUEST"
};
/** Experimental layer */
export const ExperimentalLayer = {
    BID_DECISION: "BID_DECISION",
    BID_MIN_PRICE: "BID_MIN_PRICE",
    BID_MAX_PRICE: "BID_MAX_PRICE",
    BID_PRICE_SELECTION: "BID_PRICE_SELECTION",
    BID_CANDIDATE_SELECTION: "BID_CANDIDATE_SELECTION",
    POST_BID_QUALITY_CHECK: "POST_BID_QUALITY_CHECK",
    DEMO: "DEMO"
};
/** Statuses under which an experiment holds its segment reservation; mirrors beaker's allocationHoldingStatuses. */
export const ALLOCATION_HOLDING_STATUSES = [
    "IN_REVIEW",
    "PUBLISHED",
    "ACTIVE",
    "PAUSED",
    "DRAINING"
];
/** Type of value a ConfigurationMetaVariable holds */
export var ConfigValueType;
(function (ConfigValueType) {
    ConfigValueType["STRING"] = "STRING";
    ConfigValueType["NUMBER"] = "NUMBER";
    ConfigValueType["BOOL"] = "BOOL";
    /**
     * A comma-separated list of one comprising type, described by a separate
     * ConfigurationMetaVariable keyed "{key}_arraytype" with parentConfig set
     * to this variable's id. That row is UX scaffolding only -- the SDK
     * reassembles user input into one serialized value for this key (see
     * `buildConfigurationValues`) and never sends the element row's key/value
     * to the backend on its own.
     */
    ConfigValueType["ARRAY"] = "ARRAY";
    /**
     * A colon-separated pair of comprising types (2-tuples only, for now),
     * described by two ConfigurationMetaVariable rows keyed
     * "{key}_tupletype_0"/"{key}_tupletype_1" with parentConfig set to this
     * variable's id and parentConfigIdx 0/1 respectively. Same UX-only
     * scaffolding note as ARRAY applies.
     */
    ConfigValueType["TUPLE"] = "TUPLE";
})(ConfigValueType || (ConfigValueType = {}));
/**
 * Effect a satisfied ConfigurationMetaConstraint has on its targetVariableId.
 *
 * Deliberately not declared against `GeneratedEnumValues`/the generated
 * `ConfigurationMetaConstraintKind` proto enum the way most enums in this
 * file are: like `ConfigValueType`, this value is only ever exchanged over
 * campaignmanager's REST API (never a raw gRPC call), and that REST layer
 * (see campaignmanager's configuration_meta.go and campaignmanager.yaml)
 * uses the short, unprefixed wire value for every enum it exposes -- e.g.
 * `ConfigValueType`'s "STRING"/"NUMBER", not the proto's
 * "CONFIG_VALUE_TYPE_STRING"/"CONFIG_VALUE_TYPE_NUMBER". Mirroring the full
 * proto enum name here would make a live GET response unparsable and a POST
 * from this SDK rejected as invalid.
 */
export const ConfigurationMetaConstraintKind = {
    /**
     * The target variable is pinned to `forcedValue` while this constraint is
     * active; a caller-supplied value for the target is ignored.
     */
    FORCE_VALUE: "FORCE_VALUE",
    /**
     * The target variable's own type-level bounds (min/max/permittedValues)
     * are narrowed to this constraint's restrict* fields while it is active.
     */
    RESTRICT: "RESTRICT"
};
/** Flags a SWITCHBACK setup that's likely a mistake. None of them blocks saving. */
export const SwitchbackPreviewWarning = {
    /** The cycle divides 24h or is a whole number of days, so each time of day stays on one arm. */
    CLOCK_ALIGNED: "CLOCK_ALIGNED",
    /** Some hours of the day are never reached, however long the experiment runs. */
    NEVER_COVERS_ALL_HOURS: "NEVER_COVERS_ALL_HOURS",
    /** opt_in_only is set, sampleSegments is below 10000, or participationRate is below 1. */
    SAMPLE_WIDTH_BELOW_FULL: "SAMPLE_WIDTH_BELOW_FULL",
    /** The flight is shorter than the worst-case hour coverage; see the shortfalls on SwitchbackPreviewFlight. */
    FLIGHT_SHORTER_THAN_COVERAGE: "FLIGHT_SHORTER_THAN_COVERAGE",
    /** Some weighted variants have no ID yet, so arms follow form order and may reorder once saved. */
    PROVISIONAL_ORDER: "PROVISIONAL_ORDER",
    /** Measured time is under 80% of each cycle, the rest going to gaps and burn-ins. */
    MEASURED_SHARE_BELOW_MIN: "MEASURED_SHARE_BELOW_MIN"
};
/** KPIs an optimization holdout experiment can be created for. */
export const OptimizationHoldoutKpi = {
    CVR: "CVR",
    CTR: "CTR",
    LTR: "LTR",
    VCR: "VCR"
};
//# sourceMappingURL=types.js.map